import React, { useState, useEffect } from 'react';
import { Card, Button, Spin, message, Statistic, Row, Col } from 'antd';
import { ReloadOutlined, DownloadOutlined } from '@ant-design/icons';
import { Scatter } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
} from 'chart.js';
import apiClient from '../../../utils/apiClient';
import { downloadCSV } from '../../../utils/csvExport';

ChartJS.register(LinearScale, PointElement, LineElement, Tooltip, Legend);

interface ScatterDataPoint {
  transaction_id: number;
  discount_amount: number;
  revenue: number;
  original_total: number;
  coupon_name: string;
  discount_type: string;
  discount_value: number;
  transaction_date: string;
}

interface SummaryData {
  totalTransactions: number;
  avgDiscount: number;
  avgRevenue: number;
  totalDiscount: number;
  totalRevenue: number;
}

const DiscountRevenueScatter: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ScatterDataPoint[]>([]);
  const [summary, setSummary] = useState<SummaryData | null>(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/coupon-analytics/analytics/discount-vs-revenue');

      if (response.data.success) {
        setData(response.data.data.scatterData);
        setSummary(response.data.data.summary);
      } else {
        message.error('データ取得に失敗しました');
      }
    } catch (error) {
      console.error('値引き額vs売上額分析エラー:', error);
      message.error('データ取得エラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleExportCSV = () => {
    const csvData = data.map(row => ({
      '取引ID': row.transaction_id,
      'クーポン名': row.coupon_name,
      '値引き額': `¥${row.discount_amount.toLocaleString()}`,
      '売上額（値引き後）': `¥${row.revenue.toLocaleString()}`,
      '元の合計額': `¥${row.original_total.toLocaleString()}`,
      '取引日': row.transaction_date
    }));

    downloadCSV(csvData, '値引き額vs売上額分析.csv');
    message.success('CSVファイルをダウンロードしました');
  };

  // 散布図データ
  const scatterChartData = {
    datasets: [
      {
        label: '値引き額 vs 売上額',
        data: data.map(d => ({
          x: d.discount_amount,
          y: d.revenue
        })),
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        borderColor: 'rgba(75, 192, 192, 1)',
        pointRadius: 5,
        pointHoverRadius: 7
      }
    ]
  };

  const scatterOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            const dataPoint = data[context.dataIndex];
            return [
              `クーポン: ${dataPoint.coupon_name}`,
              `値引き額: ¥${dataPoint.discount_amount.toLocaleString()}`,
              `売上額: ¥${dataPoint.revenue.toLocaleString()}`,
              `取引日: ${dataPoint.transaction_date}`
            ];
          }
        }
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: '値引き額（円）',
          font: {
            size: 14,
            weight: 'bold' as const
          }
        },
        beginAtZero: true
      },
      y: {
        title: {
          display: true,
          text: '売上額（円）',
          font: {
            size: 14,
            weight: 'bold' as const
          }
        },
        beginAtZero: true
      }
    }
  };

  return (
    <div>
      {/* アクションボタン */}
      <Card className="mb-4">
        <div className="flex justify-end gap-2">
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchData}
            loading={loading}
          >
            更新
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExportCSV}
            disabled={data.length === 0}
          >
            CSV出力
          </Button>
        </div>
      </Card>

      {/* 説明カード */}
      <Card className="mb-4 bg-blue-50">
        <div className="flex items-start gap-3">
          <div className="text-2xl">💡</div>
          <div>
            <div className="font-bold text-gray-800 mb-1">値引き額vs売上額分析とは？</div>
            <div className="text-sm text-gray-600">
              クーポンの値引き額と最終的な売上額の関係を可視化します。
              値引きが多いほど売上が増えるか、逆に減るかなどの傾向を把握できます。
            </div>
          </div>
        </div>
      </Card>

      {loading ? (
        <Card>
          <div className="flex justify-center items-center" style={{ minHeight: 400 }}>
            <Spin size="large" />
          </div>
        </Card>
      ) : summary ? (
        <>
          {/* サマリー統計 */}
          <Row gutter={16} className="mb-4">
            <Col span={6}>
              <Card>
                <Statistic
                  title="総取引数"
                  value={summary.totalTransactions}
                  suffix="件"
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="平均値引き額"
                  value={summary.avgDiscount}
                  prefix="¥"
                  valueStyle={{ color: '#cf1322' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="平均売上額"
                  value={summary.avgRevenue}
                  prefix="¥"
                  valueStyle={{ color: '#3f8600' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="総値引き額"
                  value={summary.totalDiscount}
                  prefix="¥"
                  valueStyle={{ color: '#faad14' }}
                />
              </Card>
            </Col>
          </Row>

          {/* 散布図 */}
          <Card title="値引き額 vs 売上額（散布図）">
            {data.length > 0 ? (
              <Scatter data={scatterChartData} options={scatterOptions} />
            ) : (
              <div className="text-center text-gray-500 py-12">
                データがありません
              </div>
            )}
          </Card>
        </>
      ) : (
        <Card>
          <div className="text-center text-gray-500 py-12">
            「更新」ボタンをクリックしてデータを読み込んでください
          </div>
        </Card>
      )}
    </div>
  );
};

export default DiscountRevenueScatter;
